import React from "react";

const SlackIntegration: React.FC = () => {
  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Column: Copy */}
          <div className="order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#E8F3FF] rounded-full mb-6">
              <svg className="w-4 h-4 text-[#611f69]" viewBox="0 0 24 24" fill="currentColor">
                <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
              </svg>
              <span className="text-sm font-medium text-[#611f69]">Slack Integration</span>
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              Create presentations without leaving Slack
            </h2>

            <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed">
              Generate AI-powered presentations directly from your Slack workspace—no context switching required.
            </p>

            {/* Key Capabilities */}
            <div className="space-y-4 mb-10">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#E8F3FF] flex items-center justify-center mt-0.5">
                  <svg className="w-4 h-4 text-[#3667B2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-base md:text-lg">
                    Generate presentations with a simple command
                  </h3>
                  <p className="text-gray-600 text-sm md:text-base mt-1">
                    Type your topic and get a complete presentation in seconds
                  </p>
                </div>
              </div>


              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#E8F3FF] flex items-center justify-center mt-0.5">
                  <svg className="w-4 h-4 text-[#3667B2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-base md:text-lg">
                    Export to PowerPoint or Google Slides instantly
                  </h3>
                  <p className="text-gray-600 text-sm md:text-base mt-1">
                    Download presentation-ready slides with one click
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#E8F3FF] flex items-center justify-center mt-0.5">
                  <svg className="w-4 h-4 text-[#3667B2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-base md:text-lg">
                    Collaborate in real-time with your team
                  </h3>
                  <p className="text-gray-600 text-sm md:text-base mt-1">
                    Get instant feedback and iterate faster in shared channels
                  </p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <a
                href="https://slack.zynth.ai/slack/presentation/install"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#3667B2] text-white font-semibold rounded-lg hover:bg-[#2c56a0] transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
                </svg>
                Add to Slack
              </a>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span>Works with existing Slack workspaces</span>
              </div>
            </div>
          </div>

          {/* Right Column: Slack UI Mock */}
          <div className="order-1 lg:order-2">
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
              {/* Slack Header */}
              <div className="bg-[#4A154B] px-4 py-3 flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-white/80"></div>
                  <div className="w-2 h-2 rounded-full bg-white/80"></div>
                  <div className="w-2 h-2 rounded-full bg-white/80"></div>
                </div>
                <span className="text-white font-semibold text-sm">#marketing-team</span>
              </div>

              {/* Slack Messages */}
              <div className="p-4 space-y-4 bg-gray-50 max-h-[500px] overflow-y-auto">
                {/* User Message */}
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold flex-shrink-0">
                    JS
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="font-bold text-gray-900 text-sm">Jane Smith</span>
                      <span className="text-xs text-gray-500">2:34 PM</span>
                    </div>
                    <div className="bg-white rounded-lg px-3 py-2 inline-block shadow-sm border border-gray-200">
                      <code className="text-sm text-[#3667B2] font-mono">/zynth-presentation</code>
                      <span className="text-sm text-gray-700 ml-2">Q4 Product Launch Strategy</span>
                    </div>
                  </div>
                </div>

                {/* Bot Response - Processing */}
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#3667B2] flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="font-bold text-gray-900 text-sm">Zynth</span>
                      <span className="text-xs bg-[#E8F3FF] text-[#3667B2] px-2 py-0.5 rounded">APP</span>
                      <span className="text-xs text-gray-500">2:34 PM</span>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="animate-spin w-4 h-4 border-2 border-[#3667B2] border-t-transparent rounded-full"></div>
                        <span className="text-sm text-gray-700">Generating slides...</span>
                      </div>
                      <div className="text-xs text-gray-500">Analyzing topic • Creating outline • Designing slides</div>
                    </div>
                  </div>
                </div>

                {/* Bot Response - Preview Card */}
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#3667B2] flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="font-bold text-gray-900 text-sm">Zynth</span>
                      <span className="text-xs bg-[#E8F3FF] text-[#3667B2] px-2 py-0.5 rounded">APP</span>
                      <span className="text-xs text-gray-500">2:35 PM</span>
                    </div>
                    <div className="bg-white rounded-lg overflow-hidden shadow-md border border-gray-200">
                      {/* Preview Image */}
                      <div className="bg-gradient-to-br from-blue-100 via-indigo-50 to-blue-50 h-32 flex items-center justify-center relative overflow-hidden">
                        <div className="absolute inset-0 opacity-20">
                          <div className="absolute top-4 left-4 w-20 h-20 bg-[#3667B2] rounded-lg"></div>
                          <div className="absolute top-8 right-6 w-24 h-2 bg-blue-400 rounded"></div>
                          <div className="absolute top-12 right-6 w-16 h-2 bg-blue-300 rounded"></div>
                          <div className="absolute bottom-6 left-6 w-32 h-8 bg-[#3667B2] rounded"></div>
                        </div>
                        <div className="relative z-10 text-center">
                          <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-2 shadow-lg">
                            <svg className="w-6 h-6 text-[#3667B2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <span className="text-sm font-semibold text-gray-700">12 slides created</span>
                        </div>
                      </div>
                      {/* Card Content */}
                      <div className="p-4">
                        <h4 className="font-bold text-gray-900 mb-2">Q4 Product Launch Strategy</h4>
                        <p className="text-sm text-gray-600 mb-3">
                          12 slides • Professional theme • Ready to present
                        </p>
                        <div className="flex gap-2">
                          <button className="flex-1 bg-[#3667B2] text-white text-sm font-semibold py-2 px-4 rounded-lg hover:bg-[#2c56a0] transition-colors">
                            View Presentation
                          </button>
                          <button className="bg-gray-100 text-gray-700 text-sm font-semibold py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors">
                            Export
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SlackIntegration;
